<script lang="ts">

  import CameraIcon from "@lucide/svelte/icons/camera"
  import CopyIcon from "@lucide/svelte/icons/copy"
  import ExternalLinkIcon from "@lucide/svelte/icons/external-link"
  import { Button } from "$lib/components/ui/button"
  import { Checkbox } from "$lib/components/ui/checkbox"
  import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "$lib/components/ui/dialog"
  import { Label } from "$lib/components/ui/label"
  import { SeparatorLabel } from "$lib/components/ui/separator-label"
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "$lib/components/ui/table"
  import { toast } from "svelte-sonner"
  import type { Activity, Coordinate } from "../_types"

  let {
    open,
    setOpen,
    select,
  }: {
    open: boolean
    setOpen: (value: boolean) => void
    select: Activity | undefined
  } = $props()

  async function copyCoordinate(coordinate: Coordinate | undefined) {
    if (!coordinate) return
    await navigator.clipboard.writeText(`${coordinate.latitude},${coordinate.longitude}`)
    toast.info("Koordinat berhasil disalin")
  }

  function handleCoordinate(coordinate: Coordinate | undefined) {
    if (!coordinate) return
    window.open(
      `https://maps.google.com/maps/search/?api=1&query=${coordinate.latitude},${coordinate.longitude}`,
      "_blank",
    )
  }

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Detail</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    <div class="space-y-3 text-sm">
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Nomor</Label>
          <div class="text-desc font-mono">{select?.number || "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Tanggal</Label>
          <div class="text-desc font-mono">
            {select?.date.toDateFormat("dd/MM/yyyy", "-") || "-"}
          </div>
        </div>
      </div>
      <div>
        <Label>{select?.customer_type.name || "Tipe"}</Label>
        <div class="text-desc">{select?.customer.name || "-"}</div>
      </div>
      <SeparatorLabel label={select ? (select.is_plan ? "Terencana" : "Tidak Terencana") : "-"}/>
      <div>
        <Label>Aktifitas Lain</Label>
        <div class="text-desc">{select ? (select.is_other ? "Ya" : "Tidak") : "-"}</div>
      </div>
      <div>
        <Label>Kategori</Label>
        <div class="text-desc">{select?.category.name || "-"}</div>
      </div>
      <div>
        <Label>Aktifitas</Label>
        <div class="text-desc">{select?.name || "-"}</div>
      </div>
      <div>
        <Label>Keterangan</Label>
        <div class="text-desc">{select?.description || "-"}</div>
      </div>
      <div>
        <Label>Tanggal</Label>
        <div class="text-desc font-mono">
          {select?.date.toDateFormat("dd/MM/yyyy", "-") || "-"}
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Mulai</Label>
          <div class="text-desc font-mono">
            {select?.at_start.toDateFormat("HH:mm", "-") || "-"}
          </div>
        </div>
        <div class="flex-1">
          <Label>Selesai</Label>
          <div class="text-desc font-mono">
            {select?.at_end.toDateFormat("HH:mm", "-") || "-"}
          </div>
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Rutin</Label>
          <div class="text-desc">{select?.routine.name || "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Hari</Label>
          <div class="text-desc">{select?.routine_day.name || "-"}</div>
        </div>
      </div>
      <div>
        <Label>Tahap</Label>
        <div class="text-desc">{select?.stage.name || "-"}</div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Approval Status</Label>
          <div class="text-desc">{select?.plan_approval_status.name || "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Waktu</Label>
          <div class="text-desc font-mono">
            {select?.plan_approval_at.toDateFormat("dd/MM/yyyy HH:mm", "-") || "-"}
          </div>
        </div>
      </div>
      <div>
        <Label>Oleh</Label>
        <div class="text-desc">{select?.plan_approval_by.name || "-"}</div>
      </div>
      <div>
        <Label>Catatan</Label>
        <div class="text-desc">{select?.plan_approval_description || "-"}</div>
      </div>
      <SeparatorLabel label="Realisasi"/>
      <div>
        <Label>Status</Label>
        <div class="text-desc">{select?.status.name || "Belum dilaksanakan"}</div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Mulai</Label>
          <div class="text-desc font-mono">
            {select?.real_start_datetime.toDateFormat("dd/MM/yyyy HH:mm", "-") || "-"}
          </div>
        </div>
        <div class="flex-1">
          <Label>Selesai</Label>
          <div class="text-desc font-mono">
            {select?.real_end_datetime.toDateFormat("dd/MM/yyyy HH:mm", "-") || "-"}
          </div>
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1 space-y-1">
          <Label>Koordinat</Label>
          <div class="flex gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              disabled={!select?.real_start_coordinate.longitude || !select?.real_start_coordinate.latitude}
              onclick={() => copyCoordinate(select?.real_start_coordinate)}>
              <CopyIcon aria-hidden="true"/>
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              disabled={!select?.real_start_coordinate.longitude || !select?.real_start_coordinate.latitude}
              onclick={() => handleCoordinate(select?.real_start_coordinate)}>
              <ExternalLinkIcon aria-hidden="true"/>
            </Button>
          </div>
        </div>
        <div class="flex-1 space-y-1">
          <Label>Koordinat</Label>
          <div class="flex gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              disabled={!select?.real_end_coordinate.longitude || !select?.real_end_coordinate.latitude}
              onclick={() => copyCoordinate(select?.real_end_coordinate)}>
              <CopyIcon aria-hidden="true"/>
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              disabled={!select?.real_end_coordinate.longitude || !select?.real_end_coordinate.latitude}
              onclick={() => handleCoordinate(select?.real_end_coordinate)}>
              <ExternalLinkIcon aria-hidden="true"/>
            </Button>
          </div>
        </div>
      </div>
      <div>
        <Label>Catatan</Label>
        <div class="text-desc">{select?.real_description || "-"}</div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Approval Status</Label>
          <div class="text-desc">{select?.real_approval_status.name || "-"}</div>
        </div>
        <div class="flex-1">
          <Label>Waktu</Label>
          <div class="text-desc font-mono">
            {select?.real_approval_at.toDateFormat("dd/MM/yyyy HH:mm", "-") || "-"}
          </div>
        </div>
      </div>
      <div>
        <Label>Oleh</Label>
        <div class="text-desc">{select?.real_approval_by.name || "-"}</div>
      </div>
      <div>
        <Label>Catatan</Label>
        <div class="text-desc">{select?.real_approval_description || "-"}</div>
      </div>
      {#if select && select.real_next.id > 0}
        <SeparatorLabel label="Kendala"/>
        <div>
          <Label>Kendala</Label>
          <div class="text-desc">{select.real_problem.name || "-"}</div>
        </div>
        <div>
          <Label>Catatan Kendala</Label>
          <div class="text-desc">{select.real_problem_description || "-"}</div>
        </div>
      {/if}
      {#if select && select.real_next.id > 0}
        <SeparatorLabel label="Tahapan Selanjutnya"/>
        <div>
          <Label>Tahapan</Label>
          <div class="text-desc">{select.real_next.name || "-"}</div>
        </div>
        <div>
          <Label>Tanggal</Label>
          <div class="text-desc font-mono">{select.real_next_date.toDateFormat("dd/MM/yyyy", "-")}</div>
        </div>
      {/if}
      {#if select && select.documentation_assets.isNotEmpty()}
        <Table>
          <TableHeader class="select-none">
            <TableRow class="bg-background hover:bg-background">
              <TableHead class="text-center">Foto</TableHead>
              <TableHead class="w-full text-start">Aset</TableHead>
              <TableHead class="text-end py-1.5">Kondisi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {#each select.documentation_assets as documentation (documentation.id)}
              <TableRow class="border-b-0">
                <TableCell class="text-center">
                  <Button
                    variant={documentation.document_url ? "default" : "outline"}
                    size="icon-sm"
                    class="p-0 shadow-none"
                    disabled={!documentation.document_url}
                    onclick={() => documentation.document_url.fileOpen()}>
                    <CameraIcon aria-hidden="true"/>
                  </Button>
                </TableCell>
                <TableCell class="text-start text-xs whitespace-normal">
                  <div class="text-black">{documentation.asset.name || "-"}</div>
                  <div class="text-muted-foreground">{documentation.description || "-"}</div>
                </TableCell>
                <TableCell class="flex justify-center">
                  <Checkbox
                    class="flex-wrap shadow-none"
                    checked={documentation.status}
                    disabled/>
                </TableCell>
              </TableRow>
            {/each}
          </TableBody>
        </Table>
      {/if}
      {#if select && select.documentation_assets.isNotEmpty()}
        <Table>
          <TableHeader class="select-none">
            <TableRow class="bg-background hover:bg-background">
              <TableHead class="text-center min-w-[3rem]">Foto</TableHead>
              <TableHead class="w-full text-start">Aktifitas Lain</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {#each select.documentation_others as documentation (documentation.id)}
              <TableRow class="border-b-0">
                <TableCell class="text-center">
                  <Button
                    variant={documentation.document_url ? "default" : "outline"}
                    size="icon-sm"
                    class="p-0 shadow-none"
                    disabled={!documentation.document_url}
                    onclick={() => documentation.document_url.fileOpen()}>
                    <CameraIcon aria-hidden="true"/>
                  </Button>
                </TableCell>
                <TableCell class="text-start text-sm whitespace-normal">
                  {documentation.name || "-"}
                </TableCell>
              </TableRow>
            {/each}
          </TableBody>
        </Table>
      {/if}
      {#if select && select.documentations.isNotEmpty()}
        <SeparatorLabel label="Dokumentasi Tambahan"/>
        <div class="space-y-2">
          {#each select.documentations as documentation, index (documentation.id)}
            <Button
              variant="outline"
              class="w-full justify-start"
              onclick={() => documentation.document_url.fileOpen()}>
              Dokumentasi {index + 1}
            </Button>
          {/each}
        </div>
      {/if}
    </div>
  </DialogContent>
</Dialog>
